import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiClient } from '../../lib/apiClient';
import { formatDate, parseFirestoreDate } from '../../lib/dateUtils';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../../lib/videoUtils';

function Sermons({ user }) {
  const [church, setChurch] = useState(null);
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSermon, setEditingSermon] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    audioUrl: '',
    speakerName: '',
    duration: '',
    tags: '',
    sermonDate: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myChurches = await apiClient.getMyChurches(user.id);
        if (myChurches.length === 0) {
          setError('You are not assigned to any church');
          return;
        }

        const currentChurch = myChurches[0];
        setChurch(currentChurch);

        const data = await apiClient.getSermons(currentChurch.id);
        setSermons(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleOpenDialog = (sermon = null) => {
    if (sermon) {
      setEditingSermon(sermon);
      setFormData({
        title: sermon.title || '',
        description: sermon.description || '',
        videoUrl: sermon.videoUrl || '',
        audioUrl: sermon.audioUrl || '',
        speakerName: sermon.speakerName || '',
        duration: sermon.duration || '',
        tags: sermon.tags?.join(', ') || '',
        sermonDate: parseFirestoreDate(sermon.sermonDate)?.toISOString().split('T')[0] || ''
      });
    } else {
      setEditingSermon(null);
      setFormData({
        title: '',
        description: '',
        videoUrl: '',
        audioUrl: '',
        speakerName: '',
        duration: '',
        tags: '',
        sermonDate: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSermon(null);
    setFormData({
      title: '',
      description: '',
      videoUrl: '',
      audioUrl: '',
      speakerName: '',
      duration: '',
      tags: '',
      sermonDate: ''
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const sermonData = {
        title: formData.title,
        description: formData.description,
        videoUrl: formData.videoUrl || null,
        audioUrl: formData.audioUrl || null,
        speakerName: formData.speakerName,
        duration: formData.duration ? parseInt(formData.duration) : 0,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        sermonDate: formData.sermonDate || new Date().toISOString()
      };

      if (editingSermon) {
        await apiClient.updateSermon(editingSermon.id, sermonData, user.id);
      } else {
        await apiClient.createSermon(church.id, sermonData, user.id);
      }
      
      // Refresh sermons
      const data = await apiClient.getSermons(church.id);
      setSermons(data);
      handleCloseDialog();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sermonId) => {
    if (!window.confirm('Are you sure you want to delete this sermon?')) {
      return;
    }

    try {
      await apiClient.deleteSermon(sermonId, user.id);
      setSermons(sermons.filter(s => s.id !== sermonId));
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: 2 
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Sermons
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Create Sermon
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
        {sermons.map((sermon) => (
          <Card key={sermon.id}>
            <CardContent>
              <Typography variant="h6">{sermon.title}</Typography>
              {sermon.speakerName && (
                <Typography variant="body2" color="text.secondary">
                  Speaker: {sermon.speakerName}
                </Typography>
              )}
              {sermon.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {sermon.description}
                </Typography>
              )}
              {sermon.videoUrl && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  {isYouTubeUrl(sermon.videoUrl) ? (
                    <Box
                      sx={{
                        position: 'relative',
                        paddingBottom: '56.25%', // 16:9 aspect ratio
                        height: 0,
                        overflow: 'hidden',
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        component="iframe"
                        src={getYouTubeEmbedUrl(sermon.videoUrl)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </Box>
                  ) : (
                    <Box
                      component="video"
                      controls
                      src={sermon.videoUrl}
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: 1,
                      }}
                    >
                      Your browser does not support the video tag.
                    </Box>
                  )}
                </Box>
              )}
              {sermon.audioUrl && (
                <Box sx={{ mt: 1 }}>
                  <a href={sermon.audioUrl} target="_blank" rel="noopener noreferrer">
                    Listen to Audio
                  </a>
                </Box>
              )}
              {sermon.tags && sermon.tags.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {sermon.tags.map((tag, idx) => (
                    <Typography key={idx} variant="caption" sx={{ bgcolor: 'grey.200', px: 1, py: 0.5, borderRadius: 1 }}>
                      {tag}
                    </Typography>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formatDate(sermon.sermonDate)}
              </Typography>
            </CardContent>
            <CardActions>
              <IconButton size="small" onClick={() => handleOpenDialog(sermon)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleDelete(sermon.id)}>
                <DeleteIcon />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSermon ? 'Edit Sermon' : 'Create Sermon'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Speaker Name"
              value={formData.speakerName}
              onChange={(e) => setFormData({ ...formData, speakerName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            />
            <TextField
              fullWidth
              label="Video URL"
              value={formData.videoUrl}
              onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
              placeholder="YouTube, Vimeo, etc."
            />
            <TextField
              fullWidth
              label="Audio URL"
              value={formData.audioUrl}
              onChange={(e) => setFormData({ ...formData, audioUrl: e.target.value })}
            />
            <TextField
              fullWidth
              label="Tags (comma separated)"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="faith, prayer, love"
            />
            <TextField
              fullWidth
              label="Sermon Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.sermonDate}
              onChange={(e) => setFormData({ ...formData, sermonDate: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.title}
          >
            {saving ? <CircularProgress size={24} /> : (editingSermon ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Sermons;

